import { useMemo, useState } from "react";
import { useHeatmapHistory, useSyncSources } from "../hooks/useMonitoring";
import type { GridSource } from "../../../api/monitoring";

const CELL_H = 16;
const CELL_W = 12;
const GAP = 2;
const DATE_LABEL_W = 80;
const BA_HEADER_H = 64;
const BAND_GAP = 12;

const SOURCE_TABS: { key: GridSource; label: string }[] = [
  { key: "all", label: "All Regions" },
  { key: "eia", label: "US" },
  { key: "entsoe", label: "EU" },
  { key: "bmrs", label: "UK" },
  { key: "lmp", label: "LMP" },
];

const REAL_SOURCES = SOURCE_TABS
  .map((t) => t.key)
  .filter((k): k is Exclude<GridSource, "all"> => k !== "all");

/**
 * "data"    — confirmed rows exist for this source.
 * "no_data" — the sources check ran cleanly and confirmed there's nothing.
 * "unknown" — we can't vouch either way (still loading, the sources request
 *             failed outright, or this source's own sub-check errored even
 *             though the request as a whole returned 200).
 * Keeping these distinct means a tab badge never has to guess: it either
 * knows there's data, knows there isn't, or says so honestly.
 */
type SourceStatus = "data" | "no_data" | "unknown";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function cellColor(completeness: number): string {
  if (completeness >= 0.95) return "#22c55e"; // green
  if (completeness >= 0.5) return "#eab308"; // yellow
  if (completeness > 0) return "#ef4444"; // red
  return "#1e293b"; // gray / no data
}

/** Format period label based on resolution */
function formatPeriodLabel(period: string, resolution: string): string {
  if (resolution === "yearly") return period; // "2024"
  if (resolution === "monthly") {
    // "2025-03" → "Mar 2025"
    const [y, m] = period.split("-");
    return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
  }
  // daily: "2026-02-15" → "Feb 15"
  const [, m, d] = period.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

/** Resolution label for band dividers */
function bandLabel(resolution: string): string {
  if (resolution === "yearly") return "YEARLY";
  if (resolution === "monthly") return "MONTHLY";
  return "DAILY";
}

export default function SyncHeatmap() {
  const [source, setSource] = useState<GridSource>("all");
  const { data, isLoading, error: heatmapError } = useHeatmapHistory(source);
  const { data: sourcesData, error: sourcesError } = useSyncSources();
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    ba: string;
    period: string;
    resolution: string;
    hours: number;
    hoursExpected: number;
    completeness: number;
  } | null>(null);

  // Build lookup: `${period}|${ba}` → status
  const lookup = useMemo(() => {
    const m = new Map<
      string,
      { hours_with_data: number; hours_expected: number; completeness: number }
    >();
    if (!data?.bands) return m;
    for (const band of data.bands) {
      for (const d of band.data) {
        m.set(`${d.period}|${d.ba_code}`, {
          hours_with_data: d.hours_with_data,
          hours_expected: d.hours_expected,
          completeness: d.completeness,
        });
      }
    }
    return m;
  }, [data]);

  // One status per tab, computed once. Individual sources are looked up
  // directly from the /sources response; "all" is folded from the other four
  // (real data anywhere beats an unknown elsewhere; unknown anywhere beats a
  // clean "no data" everywhere).
  const sourceStatus = useMemo((): Record<GridSource, SourceStatus> => {
    const status = {} as Record<GridSource, SourceStatus>;

    if (sourcesError) {
      // A failed request tells us nothing about any individual source —
      // don't fall back to a possibly-stale cached "has data" reading.
      for (const key of REAL_SOURCES) status[key] = "unknown";
    } else {
      const failedSubChecks = new Set(
        (sourcesData?.errors ?? []).map((e) => e.split(":")[0])
      );
      const byName = new Map((sourcesData?.sources ?? []).map((s) => [s.name, s]));

      for (const key of REAL_SOURCES) {
        const info = byName.get(key);
        if (!info || failedSubChecks.has(key)) {
          status[key] = "unknown";
        } else {
          status[key] = info.table_exists && info.row_count > 0 ? "data" : "no_data";
        }
      }
    }

    const values = REAL_SOURCES.map((k) => status[k]);
    status.all = values.includes("data")
      ? "data"
      : values.includes("unknown")
      ? "unknown"
      : "no_data";

    return status;
  }, [sourcesData, sourcesError]);

  const hasErrors = (data?.errors?.length ?? 0) > 0;

  // Mutually exclusive by construction — no combination of booleans to keep
  // in sync as new states get added.
  const viewState = isLoading
    ? "loading"
    : heatmapError
    ? "hard_error"
    : !data || data.ba_codes.length === 0
    ? (hasErrors ? "soft_error" : "empty")
    : "ready";

  return (
    <div>
      {/* Source tabs */}
      <div className="flex gap-1 mb-4">
        {SOURCE_TABS.map((tab) => {
          const active = source === tab.key;
          const status = sourceStatus[tab.key];
          const badge = status === "unknown" ? "status unknown" : status === "no_data" ? "No data" : null;

          return (
            <button
              key={tab.key}
              onClick={() => setSource(tab.key)}
              className={`px-3 py-1.5 rounded text-caption font-medium transition-colors ${
                active
                  ? "bg-quantum-500/20 text-quantum-400 border border-quantum-500/30"
                  : "bg-surface-600 text-text-secondary hover:text-text-primary border border-transparent"
              }`}
            >
              {tab.label}
              {badge && !active && (
                <span className="ml-1.5 text-[10px] text-text-muted bg-surface-700 px-1 py-0.5 rounded">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading state */}
      {viewState === "loading" && (
        <div className="h-32 bg-surface-600 animate-pulse rounded" />
      )}

      {/* Hard failure: the request itself failed (network/5xx) */}
      {viewState === "hard_error" && (
        <div className="text-center py-8 text-health-400">
          <p className="text-body-sm">
            Failed to load coverage data{source !== "all" ? ` for ${SOURCE_TABS.find((t) => t.key === source)?.label}` : ""}. Try refreshing the page.
          </p>
        </div>
      )}

      {/* Soft failure: HTTP 200 but the query(ies) behind it errored, and we
          have nothing to show for it — do not render this as "no data". */}
      {viewState === "soft_error" && (
        <div className="text-center py-8 text-health-400">
          <p className="text-body-sm">
            Coverage data failed to load: {data!.errors.join(", ")}.
          </p>
        </div>
      )}

      {/* Genuinely empty: no errors reported, query just has nothing yet */}
      {viewState === "empty" && (
        <div className="text-center py-8 text-text-muted">
          <p className="text-body-sm">
            {source === "all"
              ? "No grid data available. Enable sync pipelines to start collecting data."
              : `No data collected yet for ${SOURCE_TABS.find((t) => t.key === source)?.label}. Enable the ${source} sync pipeline to start collecting data.`}
          </p>
        </div>
      )}

      {/* Partial failure: some data loaded, but part of it failed */}
      {viewState === "ready" && hasErrors && (
        <p className="text-caption text-amber-400 mb-2">
          Partial data — failed to load: {data!.errors.join(", ")}
        </p>
      )}

      {/* Multi-band heatmap */}
      {viewState === "ready" && (() => {
        const { ba_codes, bands } = data!;
        const cols = ba_codes.length;

        // Compute total SVG height
        let totalRowHeight = 0;
        for (const band of bands) {
          totalRowHeight += band.periods.length * (CELL_H + GAP);
        }
        const dividerSpace = Math.max(0, bands.length - 1) * BAND_GAP;
        const svgW = DATE_LABEL_W + cols * (CELL_W + GAP);
        const svgH = BA_HEADER_H + totalRowHeight + dividerSpace;

        // Track Y offset as we render bands top-to-bottom
        let yOffset = BA_HEADER_H;

        return (
          <div className="overflow-x-auto">
            <svg
              width={svgW}
              height={svgH}
              className="font-mono"
              onMouseLeave={() => setTooltip(null)}
            >
              {/* BA code headers (rotated 45deg) */}
              {ba_codes.map((ba, ci) => (
                <text
                  key={ba}
                  x={DATE_LABEL_W + ci * (CELL_W + GAP) + CELL_W / 2}
                  y={BA_HEADER_H - 4}
                  textAnchor="end"
                  fontSize={9}
                  fill="#94a3b8"
                  transform={`rotate(-45, ${DATE_LABEL_W + ci * (CELL_W + GAP) + CELL_W / 2}, ${BA_HEADER_H - 4})`}
                >
                  {ba}
                </text>
              ))}

              {/* Render each band */}
              {bands.map((band, bi) => {
                const bandStartY = yOffset;
                const bandRows = band.periods.length;
                const bandContent = (
                  <g key={band.resolution}>
                    {/* Band divider line (above all bands except the first) */}
                    {bi > 0 && (
                      <>
                        <line
                          x1={0}
                          y1={bandStartY - BAND_GAP / 2}
                          x2={svgW}
                          y2={bandStartY - BAND_GAP / 2}
                          stroke="#475569"
                          strokeWidth={1}
                          strokeDasharray="4 3"
                        />
                        <text
                          x={4}
                          y={bandStartY - BAND_GAP / 2 - 2}
                          fontSize={8}
                          fill="#64748b"
                          fontWeight="bold"
                        >
                          {bandLabel(band.resolution)}
                        </text>
                      </>
                    )}
                    {/* First band label (no divider above it) */}
                    {bi === 0 && (
                      <text
                        x={4}
                        y={bandStartY - 2}
                        fontSize={8}
                        fill="#64748b"
                        fontWeight="bold"
                      >
                        {bandLabel(band.resolution)}
                      </text>
                    )}

                    {/* Rows for this band */}
                    {band.periods.map((period, ri) => {
                      const rowY = bandStartY + ri * (CELL_H + GAP);
                      const label = formatPeriodLabel(period, band.resolution);
                      return (
                        <g key={period}>
                          <text
                            x={DATE_LABEL_W - 4}
                            y={rowY + CELL_H / 2 + 4}
                            textAnchor="end"
                            fontSize={10}
                            fill="#94a3b8"
                          >
                            {label}
                          </text>
                          {ba_codes.map((ba, ci) => {
                            const x = DATE_LABEL_W + ci * (CELL_W + GAP);
                            const entry = lookup.get(`${period}|${ba}`);
                            const completeness = entry?.completeness ?? 0;
                            const hours = entry?.hours_with_data ?? 0;
                            const hoursExpected = entry?.hours_expected ?? 0;
                            return (
                              <rect
                                key={`${period}-${ba}`}
                                x={x}
                                y={rowY}
                                width={CELL_W}
                                height={CELL_H}
                                rx={2}
                                fill={cellColor(completeness)}
                                opacity={completeness > 0 ? 0.9 : 0.3}
                                onMouseEnter={(e) => {
                                  const rect = (
                                    e.target as SVGRectElement
                                  ).getBoundingClientRect();
                                  setTooltip({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top,
                                    ba,
                                    period,
                                    resolution: band.resolution,
                                    hours,
                                    hoursExpected,
                                    completeness,
                                  });
                                }}
                                onMouseLeave={() => setTooltip(null)}
                                className="cursor-pointer"
                              />
                            );
                          })}
                        </g>
                      );
                    })}
                  </g>
                );

                // Advance yOffset for next band
                yOffset += bandRows * (CELL_H + GAP);
                if (bi < bands.length - 1) {
                  yOffset += BAND_GAP;
                }

                return bandContent;
              })}
            </svg>

            {/* Tooltip (positioned via fixed) */}
            {tooltip && (
              <div
                className="fixed z-50 px-2.5 py-1.5 rounded bg-surface-800 border border-border text-caption text-text-primary shadow-lg pointer-events-none"
                style={{
                  left: tooltip.x,
                  top: tooltip.y - 44,
                  transform: "translateX(-50%)",
                }}
              >
                <span className="font-semibold">{tooltip.ba}</span>{" "}
                <span className="text-text-secondary">
                  {formatPeriodLabel(tooltip.period, tooltip.resolution)}
                </span>
                <br />
                {tooltip.hours.toLocaleString()}h / {tooltip.hoursExpected.toLocaleString()}h (
                {Math.round(tooltip.completeness * 100)}%)
              </div>
            )}
          </div>
        );
      })()}

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 text-caption text-text-secondary">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#22c55e" }} />
          &ge;95%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#eab308" }} />
          &ge;50%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#ef4444" }} />
          &gt;0%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#1e293b", opacity: 0.3 }} />
          No data
        </span>
      </div>
    </div>
  );
}
